import { client } from './backend/client'
import { REDIRECT_QUERY } from './guards'
import router from './router'
import store, { Session, SessionUser } from './store'

// What `POST /api/auth/signin` answers with. Every field the response carries,
// which the backend pins: a token, and an account with nothing on it that is not
// needed to render a user menu and decide whether to offer administrative
// affordances. No password material, no active flag.
interface SignInResponse {
  id: string
  name: string
  email: string
  username: string
  // eslint-disable-next-line camelcase
  is_admin: boolean
  token: string
}

// A refusal, and nothing else. The server answers a wrong password, an unknown
// account and a disabled account identically -- byte for byte, so that the form
// cannot be used to find out which accounts exist -- and it answers with an empty
// body, so there is nothing to report that one refusal could say and another
// could not. Hence one message: it tells the user their sign-in did not happen,
// and says nothing at all about why.
//
// Anything that is not a refusal from the server -- a network that is not there,
// a response with no status -- is left to propagate as itself, because reporting
// it as a refused sign-in would be a lie about a credential nobody refused.
export class SignInRefused extends Error {
  constructor () {
    super('Those credentials were not accepted.')
    this.name = 'SignInRefused'
  }
}

// One field takes either identifier, and the server matches each of them
// without regard to capitalisation, so the only thing to decide here is which of
// the two is being sent -- it refuses a body naming both, so it cannot be both.
// An `@` is what tells them apart. The server length-checks a username and
// nothing else, so that is a test of shape rather than a rule the server states,
// which is worth saying out loud: it is the one decision in this module that
// could be wrong, and a username that happens to contain an `@` would be sent as
// an email address and refused.
function signInBody (identifier: string, password: string) {
  if (identifier.indexOf('@') === -1) {
    return { username: identifier, password }
  }

  return { email: identifier, password }
}

// Named field by field rather than spread, so that a field the server adds later
// does not arrive in the store without anyone deciding it should.
function sessionFrom (response: SignInResponse): Session {
  const user: SessionUser = {
    id: response.id,
    name: response.name,
    email: response.email,
    username: response.username,
    isAdmin: response.is_admin
  }

  return { token: response.token, user }
}

// Posts the credentials and starts the session, or reports a refusal. What
// happens to the visitor afterwards is not this function's business -- signing in
// navigates away and re-authenticating must not -- so the two callers below share
// this and differ only in what they do once it has answered.
async function authenticate (identifier: string, password: string): Promise<void> {
  let response

  try {
    response = await client.post('/auth/signin', signInBody(identifier, password))
  } catch (error) {
    if ((error as any).response !== undefined) {
      throw new SignInRefused()
    }

    throw error
  }

  store.commit('sessionStarted', sessionFrom(response.data))
}

// Signs in, and leaves. The navigation is here rather than in the view so that
// "signing in goes somewhere" is one thing with one answer, and so that a
// component that forgot to navigate cannot leave a user on a form they have
// already filled in correctly.
export async function signIn (identifier: string, password: string): Promise<void> {
  await authenticate(identifier, password)

  // `replace`, so that the back button does not return to the form, and the
  // callback form rather than the promise one: Vue Router rejects the promise
  // for a navigation to the route already shown, and turning that into a failed
  // sign-in would tell somebody who has just signed in that they have not. The
  // callbacks do nothing because there is nothing to do with them -- the session
  // is started and stored by this point, and the router is what moves the visitor
  // on from here.
  router.replace(landing(), () => {}, () => {})
}

// Where a signed-in visitor lands: the route a guard turned them away from, or
// the home page.
//
// A guard writes the route it wanted into the sign-in route's query, so somebody
// who asked for their own lists and was sent here gets them rather than a home
// page they then have to navigate away from. What is read back is resolved
// against this application's routes before it is navigated to, and anything that
// matches none of them is ignored: the query is in the address bar, so it is
// something a visitor can edit, and a redirect honoured without being resolved
// would be this application handing somebody else's URL a signed-in landing.
//
// `signedIn` is not checked before following the redirect, and does not need to
// be: the guard that wrote it runs again on the way back, and a route whose guard
// turns the visitor away a second time redirects again. That is a loop only if
// the guard cannot be satisfied, and the sign-in route is the one route whose
// guard is satisfied by the session this module has just started.
function landing () {
  const asked = router.currentRoute.query[REDIRECT_QUERY]

  if (typeof asked !== 'string') {
    return { name: 'home' }
  }

  const resolved = router.resolve(asked)

  if (resolved.route.matched.length === 0) {
    return { name: 'home' }
  }

  return resolved.route.fullPath
}

// Signs in again without going anywhere.
//
// For a client that has just done something which revoked the token it was
// holding -- a password change, and that is the only such thing in the
// application. The alternative is to leave the user where they are with a
// credential the server has stopped honouring, which reads as a screen that
// works right up until they click something.
//
// Deliberately not `signIn/2`: this must not navigate to the home page. The user
// is in the middle of something -- on the profile screen, having just chosen a new
// password -- and being sent to the front page afterwards would throw away where
// they were. That is the only difference between the two, which is why they share
// everything above rather than each posting for itself.
export async function reauthenticate (identifier: string, password: string): Promise<void> {
  await authenticate(identifier, password)
}
